import manifest from "../../../../config/questionnaire-question-capabilities.json";
import type { QuestionType } from "./index";
import type { PlanVerbatimContent } from "./plan";
import { QUESTION_TYPE_IDS, QUESTION_TYPE_REGISTRY } from "./registry";

/**
 * What each question type supports, shared with Python
 * (`apps/python-api/models/questionnaire_plan/question_capabilities.py`,
 * which documents each fact). Every TypeScript reader of these facts derives
 * from here.
 */
export interface QuestionCapabilities {
  /** AUTHOR writes a member sequence, so a shared-list claim can be checked. */
  sharedListCarrying: boolean;
  /** Its typed outcomes are options a categorical condition can name by index. */
  outcomeConditionSource: boolean;
  /** It takes a bounded or unbounded selection policy. */
  selectionPolicy: boolean;
  /**
   * Its answers (a select's options, a categorical grid's categories) are
   * shown fixed or randomized (`optionOrder`), and an option list's
   * `keepLastPositions` can pin answers last. A ranked list has no such
   * choice: its items are always shown shuffled (the compiler's ranking element).
   */
  optionOrder: boolean;
  /**
   * Its answers (a select's options, a categorical grid's categories) may be
   * flagged an escape (`exclusive`, "None of these") or a catch-all kept last
   * (`keepLast`) (D0). A ranked list's may not (D0a: an escape inside a ranked
   * list is incoherent), so every ranking write strips them.
   */
  escapeAnswers: boolean;
  /**
   * Its answers (a select's options, a categorical grid's categories) may be
   * declared one ordinal sequence, in list order (`ordered`).
   */
  orderedAnswers: boolean;
  /** Its answer list may be carried forward from an earlier question's answers (`choicesFrom`). */
  choicesFromTarget: boolean;
  /** Its answers may be carried forward to a later question's answer list. */
  choicesFromSource: boolean;
  /** Its answer options or grid categories may be the tested-asset roster (`assetChoiceIntent`). */
  assetChoiceSurface: boolean;
  /** A verdict (a winner among the tested assets) can be read from its answers. */
  verdictSurface: boolean;
  /** Typed non-scored answers it may offer (per measure on a grid); null sets no count. */
  maxNonScoredStates: number | null;
  /** The literal fields AUTHOR's closed response for the type can carry. */
  verbatimFields: ReadonlyArray<keyof PlanVerbatimContent>;
}

/**
 * The group a split's only group is compared with: those who answered the
 * question without picking its answers (`splitSources.overlappingSoleGroup`).
 */
export type SplitComplement = "not_picked";

type QuestionCapabilityManifest = {
  schemaVersion: "questionnaire-question-capabilities.v1";
  matrixMaxColumns: number;
  researchQuestionFloor: number;
  researchQuestionCeiling: number;
  minutesPerQuestion: number;
  assetChoiceMinAssets: number;
  minChoiceMembers: number;
  /** The fewest fixed rows a grid has (`matrix_rows_too_few`). */
  matrixRowsFloor: number;
  rankedItemsFloor: number;
  brandCompetitorMax: number;
  splitSources: {
    partition: Array<QuestionType>;
    overlapping: Array<QuestionType>;
    band: Array<QuestionType>;
    overlappingSoleGroup: SplitComplement;
  };
  questionTypes: Record<QuestionType, QuestionCapabilities>;
};

const parsed = manifest as unknown as QuestionCapabilityManifest;
if (
  parsed.schemaVersion !== "questionnaire-question-capabilities.v1" ||
  !Number.isInteger(parsed.matrixMaxColumns) ||
  parsed.matrixMaxColumns < 1 ||
  !Number.isInteger(parsed.researchQuestionFloor) ||
  !Number.isInteger(parsed.researchQuestionCeiling) ||
  parsed.researchQuestionFloor < 1 ||
  parsed.researchQuestionFloor > parsed.researchQuestionCeiling ||
  typeof parsed.minutesPerQuestion !== "number" ||
  !(parsed.minutesPerQuestion > 0) ||
  !Number.isInteger(parsed.assetChoiceMinAssets) ||
  parsed.assetChoiceMinAssets < 1 ||
  !Number.isInteger(parsed.minChoiceMembers) ||
  parsed.minChoiceMembers < 1 ||
  !Number.isInteger(parsed.matrixRowsFloor) ||
  parsed.matrixRowsFloor < 1 ||
  !Number.isInteger(parsed.rankedItemsFloor) ||
  parsed.rankedItemsFloor < 1 ||
  !Number.isInteger(parsed.brandCompetitorMax) ||
  parsed.brandCompetitorMax < 0 ||
  JSON.stringify(Object.keys(parsed.questionTypes)) !==
    JSON.stringify(QUESTION_TYPE_IDS) ||
  !Array.isArray(parsed.splitSources?.partition) ||
  !Array.isArray(parsed.splitSources?.overlapping) ||
  !Array.isArray(parsed.splitSources?.band) ||
  parsed.splitSources.overlappingSoleGroup !== "not_picked" ||
  ![
    ...parsed.splitSources.partition,
    ...parsed.splitSources.overlapping,
    ...parsed.splitSources.band,
  ].every((type) =>
    (QUESTION_TYPE_IDS as ReadonlyArray<string>).includes(type),
  )
) {
  throw new TypeError(
    "Questionnaire question-capability registry does not match QuestionType.",
  );
}

/**
 * Most rating measures (matrix_rating) or categories (matrix_categorical) in
 * one grid. It matches the deck's matrix heatmap limit, so a grid the builder
 * accepts is one the report can draw.
 */
export const MATRIX_MAX_COLUMNS: number = parsed.matrixMaxColumns;

/**
 * Fewest research questions any draft may have: 1. There is no product
 * minimum beyond "at least one question"; only an empty set is invalid.
 * Python's PLAN stage reads the same key.
 */
export const DEFAULT_RESEARCH_QUESTION_FLOOR: number =
  parsed.researchQuestionFloor;

/** Hard ceiling on research questions; Python's PLAN stage reads the same key. */
export const RESEARCH_QUESTION_CEILING: number = parsed.researchQuestionCeiling;

/**
 * Minutes a research question adds to the questionnaire's length (the
 * registry's `minutesPerQuestion`). Python's revise and REPLAN projections
 * read the same key (`models.questionnaire_plan.question_capabilities`).
 */
export const MINUTES_PER_QUESTION: number = parsed.minutesPerQuestion;

/**
 * The questionnaire's length in minutes, as the builder header, Review and the
 * chat reply all state it: `MINUTES_PER_QUESTION` a question, rounded half up,
 * never under one minute. Python's `estimated_questionnaire_minutes` is the
 * same arithmetic, held to it by a parity test (gate 2026-10-16: the
 * frontend's copy was the only one, NA-09, MT-09; final30: revise could not
 * measure a limit in minutes, LP-04, LP-21).
 */
export function estimatedQuestionnaireMinutes(questionCount: number): number {
  return Math.max(1, Math.round(questionCount * MINUTES_PER_QUESTION));
}

/**
 * Fewest tested assets of one family an asset choice offers, and the fewest
 * participants a comparison needs; Python's PLAN stage reads the same key.
 */
export const ASSET_CHOICE_MIN_ASSETS: number = parsed.assetChoiceMinAssets;

/** Fewest answer options a choice, or categories a categorical grid, may offer. */
export const MIN_CHOICE_MEMBERS: number = parsed.minChoiceMembers;
export const MATRIX_ROWS_FLOOR: number = parsed.matrixRowsFloor;

/**
 * The fewest items a ranking's stated count may name: a stated
 * `minRankedItems` or `maxRankedItems` is at least this, and an unstated one
 * is absent (never 0). Python's draft contract reads the same key
 * (`models.questionnaire.ranking_bounds`), so the AUTHOR wire, revise's edit
 * and the stored draft are generated with it; `rankingCountRange` puts it
 * beside the option count for the write gate and the editor.
 */
export const RANKED_ITEMS_FLOOR: number = parsed.rankedItemsFloor;

/**
 * Most competitors a study's brand facts may name, until brand competitors
 * are assets. Python reads the same key and gives every model writer of
 * brand facts a `maxItems` from it.
 */
export const BRAND_COMPETITOR_MAX: number = parsed.brandCompetitorMax;

/** Per question type, in `QUESTION_TYPE_IDS` order. */
export const QUESTION_CAPABILITIES: Readonly<
  Record<QuestionType, QuestionCapabilities>
> = Object.freeze(parsed.questionTypes);

function typesWhere(
  holds: (type: QuestionType) => boolean,
): ReadonlySet<QuestionType> {
  return new Set(QUESTION_TYPE_IDS.filter(holds));
}

const content = (type: QuestionType) => QUESTION_TYPE_REGISTRY[type].content;

// What content a type's response has is the question-type registry's content
// contract (`registry.ts`), which build, preview and the twin test already
// read. The grid, rating-design and option-list sets derive from it.

/** A grid with rows and columns (`matrixIntent`). */
export const MATRIX_QUESTION_TYPES = typesWhere(
  (type) => content(type).requiresColumns,
);
/**
 * Its answers are one choice list: a select's or ranking's options, or a
 * categorical grid's categories (`requiresChoices`).
 */
export const CHOICE_LIST_QUESTION_TYPES = typesWhere(
  (type) => content(type).requiresChoices,
);
/** It takes a rating scale design (`ratingUnit`, `pointLabelsRequired`). */
export const RATING_DESIGN_QUESTION_TYPES = typesWhere(
  (type) => content(type).requiresScale,
);
/**
 * Its answers are one option list, not a grid's categories (literal or named
 * option labels, `outcomePositions`, a shared choice list).
 */
export const OPTION_LIST_QUESTION_TYPES = typesWhere(
  (type) => content(type).requiresChoices && !content(type).requiresColumns,
);
/** Its answers, or a grid's categories, are shown fixed or randomized (`optionOrder`). */
export const OPTION_ORDER_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].optionOrder,
);
/**
 * A select: its answers are one option list that may hold an escape or a
 * catch-all kept last (`escapeAnswers`; a ranking's list may not).
 */
export const SELECT_QUESTION_TYPES = typesWhere(
  (type) =>
    QUESTION_CAPABILITIES[type].escapeAnswers &&
    content(type).requiresChoices &&
    !content(type).requiresColumns,
);
/** Its answer list may be carried forward from an earlier question's answers (`choicesFromTarget`). */
export const CHOICES_FROM_TARGET_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].choicesFromTarget,
);
/** Its answers may be carried forward to a later question's answer list (`choicesFromSource`). */
export const CHOICES_FROM_SOURCE_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].choicesFromSource,
);
/** Its answers may be declared one ordinal sequence (`ordered`). */
export const ORDERED_ANSWER_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].orderedAnswers,
);
/** It takes a bounded or unbounded selection policy. */
export const SELECTION_POLICY_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].selectionPolicy,
);
/** Its typed outcomes are options a categorical condition can name by index. */
export const OUTCOME_CONDITION_SOURCE_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].outcomeConditionSource,
);
/**
 * The report can split people by its answers, as the user asks: groups of
 * answers that partition the sample, or overlapping groups (`splitSources`,
 * the one owner of which question types can be split; Python reads the same
 * key).
 */
export const SPLIT_SOURCE_QUESTION_TYPES = typesWhere((type) =>
  [...parsed.splitSources.partition, ...parsed.splitSources.overlapping].includes(type),
);
/**
 * What a split's only group is compared with, or null: on an overlapping type
 * (`splitSources.overlappingSoleGroup`), those who answered without picking
 * the group's answers. The one owner of the rule; analysis (Python) and the
 * chat's study facts both read it.
 */
export function splitComplementOf(
  questionType: unknown,
  groupCount: number,
): SplitComplement | null {
  return groupCount === 1 &&
    (parsed.splitSources.overlapping as ReadonlyArray<unknown>).includes(questionType)
    ? parsed.splitSources.overlappingSoleGroup
    : null;
}
/** Its answer options or grid categories may be the tested-asset roster. */
export const ASSET_CHOICE_SURFACE_QUESTION_TYPES = typesWhere(
  (type) => QUESTION_CAPABILITIES[type].assetChoiceSurface,
);
